import {DosageFormData} from "@/dosage/interfaces/DosageForm";
import DosageService from "@/dosage/services/DosageService";

export interface ValidationError {
    field: string;
    message: string;
}

export interface ValidationResult {
    isValid: boolean;
    errors: ValidationError[];
}

/**
 * Validate time format (HH:MM)
 */
const isValidTimeFormat = (time: string): boolean => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
};

/**
 * Service for validating dosage.
 */
const DosageValidationService = () => {

    /**
     * Validate that a product ID is provided.
     */
    const validateProductId = (productId?: number): ValidationError[] => {
        const errors: ValidationError[] = [];

        if (!productId) {
            errors.push({
                field: 'product_id',
                message: 'Product is required.'
            });
        }

        return errors;
    };

    /**
     * Validate the dose field.
     */
    const validateDose = (dose: DosageFormData['dose']): ValidationError[] => {
        const errors: ValidationError[] = [];

        if (!dose || !dose.id) {
            errors.push({
                field: 'dose',
                message: 'Une forme est requise.'
            });
        }

        return errors;
    };

    /**
     * Validate the frequency field.
     *
     * Required if no dates are set.
     */
    const validateFrequency = (frequency: DosageFormData['frequency'], dates: DosageFormData['dates']): ValidationError[] => {
        const errors: ValidationError[] = [];

        if (!frequency && (!dates || dates.length === 0)) {
            errors.push({
                field: 'frequency',
                message: 'La fréquence est requise si aucune date n\'est définie.'
            });
        } else if (frequency && !Number.isInteger(frequency.id)) {
            errors.push({
                field: 'frequency',
                message: 'La fréquence est invalide.'
            });
        }

        return errors;
    };

    /**
     * Validate the dates :
     *
     * - Must by array or nullable
     * - Required without start date and end date
     * - Min 1 value if set
     */
    const validateDates = (dates: DosageFormData["dates"], startAt: DosageFormData['start'], endAt: DosageFormData['end']) => {
        const errors: ValidationError[] = [];

        if ((!startAt || !endAt) && (!dates || dates.length === 0)) {
            errors.push({
                field: 'dates',
                message: 'Au moins une date est requise si aucune date de début et de fin n\'est définie.'
            });
        }

        return errors;
    };

    /**
     * Start date is required if no dates are set.
     * Must be a valid date.
     */
    const validateStartDate = (dates: DosageFormData['dates'], startAt: DosageFormData['start']) => {
        const errors: ValidationError[] = [];

        if (!startAt && (!dates || dates.length === 0)) {
            errors.push({
                field: 'start',
                message: 'La date de début est requise si aucune date n\'est définie.'
            });
        }

        if (startAt && isNaN(startAt.getTime())) {
            errors.push({
                field: 'start',
                message: 'La date de début est invalide.'
            });
        }

        return errors;
    };

    /**
     * Required without dates,
     * must be a valid date,
     * must be after start date.
     */
    const validateEndDate = (dates: DosageFormData['dates'], startAt: DosageFormData['start'], endAt: DosageFormData['end']) => {
        const errors: ValidationError[] = [];

        if (endAt && isNaN(endAt.getTime())) {
            errors.push({
                field: 'end',
                message: 'La date de fin est invalide.'
            });
        }

        if (startAt && endAt && endAt < startAt) {
            errors.push({
                field: 'end',
                message: 'La date de fin doit être après la date de début.'
            });
        }

        if (!endAt && (!dates || dates.length === 0)) {
            errors.push({
                field: 'end',
                message: 'La date de fin est requise si aucune date n\'est définie.'
            });
        }

        return errors;
    };

    /**
     * Validate the times array.
     */
    const validateTimes = (times: DosageFormData['times']): ValidationError[] => {
        const errors: ValidationError[] = [];

        if (!times || times.length === 0) {
            errors.push({
                field: 'times',
                message: 'Au moins un horaire est requis.'
            });
        }

        if (times && times.length > 0) {
            if (times.every((time) => Number.isNaN(time.dose) || time.dose <= 0)) {
                errors.push({
                    field: 'times',
                    message: 'Au moins une dose doit être renseignée.'
                });
            }

            times.forEach((time, index) => {
                if (Number.isNaN(time.dose)) {
                    errors.push({
                        field: `times.${index}.dose`,
                        message: 'La dose est requise.'
                    });
                }

                if (DosageService.isDosageTimeWithTrigger(time)) {
                    if (!time.dosage_time_trigger) {
                        errors.push({
                            field: `times.${index}.dosage_time_trigger_id`,
                            message: 'Le déclencheur est requis.'
                        });
                    }
                } else {
                    if (!time.time || !isValidTimeFormat(time.time)) {
                        errors.push({
                            field: `times.${index}.time`,
                            message: 'L\'horaire est requis.'
                        });
                    }
                }
            });
        }

        return errors;
    };


    return {

        /**
         * Validate the dosage form data.
         */
        validate(data: DosageFormData, productId?: number, shouldValidateDates: boolean = true): ValidationResult {
            const errors: ValidationError[] = [];

            errors.push(...validateProductId(productId));
            errors.push(...validateDose(data.dose));
            errors.push(...validateFrequency(data.frequency, data.dates));
            errors.push(...validateTimes(data.times));

            if (shouldValidateDates) {
                errors.push(...validateDates(data.dates, data.start, data.end));
                errors.push(...validateStartDate(data.dates, data.start));
                errors.push(...validateEndDate(data.dates, data.start, data.end));
            }


            return {
                isValid: errors.length === 0,
                errors
            };
        },

        /**
         * Get a single error message for a specific field
         */
        getFieldError(errors: ValidationError[], field: string): string | null {
            const error = errors.find(e => e.field === field);
            return error ? error.message : null;
        },

        /**
         * Get all error messages for fields starting with a prefix
         */
        getFieldErrors(errors: ValidationError[], fieldPrefix: string): ValidationError[] {
            return errors.filter(e => e.field.startsWith(fieldPrefix));
        },

        /**
         * Convert validation errors to a map for easier display
         */
        errorsToMap(errors: ValidationError[]): Record<string, string> {
            return errors.reduce((acc, error) => {
                acc[error.field] = error.message;
                return acc;
            }, {} as Record<string, string>);
        }
    };
};

export default DosageValidationService();
